import api from '@api';
import Chip from '@components/common/Chip';
import config from '@config';
import type { AudienceType } from '@enums';
import AudienceIcon from '@icons/audience.svg?react';
import CalendarIcon from '@icons/calendar.svg?react';
import FolderIcon from '@icons/folder.svg?react';
import InfoIcon from '@icons/info.svg?react';
import LocationIcon from '@icons/location.svg?react';
import ShapesIcon from '@icons/shapes.svg?react';
import { useShellStore } from '@store/shell';
import { SecondaryButton as TextButton } from '@ui/buttons';
import * as utils from '@utils';
import { useCallback, useState } from 'react';
import type { Audience, SourcesOverviewResponse } from 'types/schema';
import Styled from './SourceOverview.styled';

interface Props {
  data: SourcesOverviewResponse;
}

function SourceOverview({ data }: Props) {
  const shellStore = useShellStore();
  const [shouldShowMore, setShouldShowMore] = useState({
    audiences: false,
    topics: false
  });

  const handleOpenInfo = () => {
    // Show the appendix
    shellStore.setIsSourceInfoDialogOpen(!shellStore.isSourceInfoDialogOpen);
    // Track event
    api.analytics.viewedSourceInfoDialog();
  };

  const getAudienceChips = useCallback((audiences: Audience[]) => {
    return audiences.map((item, index) => {
      const { icon, description } = utils.getAudienceMetadata(
        item.type as AudienceType
      );
      const tooltip = item['is_outdated'] ? (
        <Styled.TooltipContent>
          Audience was edited after the below insights were generated
        </Styled.TooltipContent>
      ) : (
        <Styled.TooltipContent>
          <Styled.TooltipTitle>
            {icon}
            <span>{description}</span>
          </Styled.TooltipTitle>
          <p>{item.description}</p>
        </Styled.TooltipContent>
      );

      return (
        <Chip
          icon={icon}
          tooltip={tooltip}
          key={`source-overview-audience-${item.id}-${index}`}>
          {item.title}
        </Chip>
      );
    });
  }, []);

  const handleViewMoreItems = useCallback(
    (itemType: 'audiences' | 'topics') => {
      setShouldShowMore((shouldShowMore) => ({
        ...shouldShowMore,
        [itemType]: true
      }));

      // Track event
      api.analytics.viewedMoreThinkingOverviewItems();
    },
    []
  );

  const renderAudiences = useCallback(() => {
    if (!data.audiences) {
      return null;
    }

    // Avoid mutating the original array
    const copy = [...data.audiences];
    const audiences = copy.sort(({ title: a }, { title: b }) =>
      a < b ? -1 : 1
    );
    const hiddenAudiences = audiences.splice(
      config.chat.sources.audiences.visibilitySize
    );

    return (
      <>
        {getAudienceChips(audiences)}
        {hiddenAudiences.length > 0 && !shouldShowMore.audiences ? (
          <Styled.SeeMoreButtonWrapper>
            <Styled.SeeMoreButton
              data-testid="data-overview-see-more-button"
              onClick={() => handleViewMoreItems('audiences')}>
              +{hiddenAudiences.length} more
            </Styled.SeeMoreButton>
          </Styled.SeeMoreButtonWrapper>
        ) : null}
        {shouldShowMore.audiences && getAudienceChips(hiddenAudiences)}
      </>
    );
  }, [data, shouldShowMore.audiences, getAudienceChips, handleViewMoreItems]);

  const renderTopics = useCallback(() => {
    if (!data.topics) {
      return null;
    }

    const topics = [...data.topics];
    const hiddenTopics = topics.splice(
      config.chat.sources.topics.visibilitySize
    );

    return (
      <>
        {topics.map((item) => (
          <Chip key={item}>{item}</Chip>
        ))}
        {hiddenTopics.length > 0 && !shouldShowMore.topics ? (
          <Styled.SeeMoreButtonWrapper>
            <Styled.SeeMoreButton
              data-testid="data-overview-see-more-button"
              onClick={() => handleViewMoreItems('topics')}>
              +{hiddenTopics.length} more
            </Styled.SeeMoreButton>
          </Styled.SeeMoreButtonWrapper>
        ) : (
          hiddenTopics.map((item) => <Chip key={item}>{item}</Chip>)
        )}
      </>
    );
  }, [data, shouldShowMore.topics, handleViewMoreItems]);

  if (!data) {
    return null;
  }

  return (
    <Styled.Wrapper>
      <Styled.Table role="table">
        <Styled.TableRow data-testid="data-overview-audiences" role="row">
          <Styled.TableHeader role="rowheader">
            <Styled.TableHeaderLabel>
              <AudienceIcon />
              <span>Audience:</span>
            </Styled.TableHeaderLabel>
          </Styled.TableHeader>
          <Styled.TableData role="cell">{renderAudiences()}</Styled.TableData>
        </Styled.TableRow>
        {data.topics?.length > 0 && (
          <Styled.TableRow data-testid="data-overview-topics" role="row">
            <Styled.TableHeader role="rowheader">
              <Styled.TableHeaderLabel>
                <ShapesIcon />
                <span>Topic:</span>
              </Styled.TableHeaderLabel>
            </Styled.TableHeader>
            <Styled.TableData role="cell">{renderTopics()}</Styled.TableData>
          </Styled.TableRow>
        )}
        <Styled.TableRow data-testid="data-overview-timeframe" role="row">
          <Styled.TableHeader role="rowheader">
            <Styled.TableHeaderLabel>
              <CalendarIcon />
              <span>Timeframe:</span>
            </Styled.TableHeaderLabel>
          </Styled.TableHeader>
          <Styled.TableData role="cell">
            {data['timeframe_groups']?.map((item) => (
              <Chip key={item.group}>{item.group}</Chip>
            ))}
          </Styled.TableData>
        </Styled.TableRow>
        <Styled.TableRow data-testid="data-overview-locations" role="row">
          <Styled.TableHeader role="rowheader">
            <Styled.TableHeaderLabel>
              <LocationIcon />
              <span>Location:</span>
            </Styled.TableHeaderLabel>
          </Styled.TableHeader>
          <Styled.TableData role="cell">
            {data['location_groups']?.map((item) => (
              <Chip
                tooltip={
                  <Styled.TooltipContent>
                    <p>Default locations:</p>
                    <b>
                      {item.details.locations
                        ?.map((location) => location.name)
                        .join(', ')}
                    </b>
                  </Styled.TooltipContent>
                }
                key={item.group}>
                {item.group}
              </Chip>
            ))}
          </Styled.TableData>
        </Styled.TableRow>
        <Styled.TableRow data-testid="data-overview-dataset" role="row">
          <Styled.TableHeader role="rowheader">
            <Styled.TableHeaderLabel>
              <FolderIcon />
              <span>Data set:</span>
            </Styled.TableHeaderLabel>
          </Styled.TableHeader>
          <Styled.TableData role="cell">
            {data['datasets']?.map((item) => (
              <Chip
                tooltip={
                  <>
                    <Styled.TooltipContent>
                      <p>{item.description}</p>
                    </Styled.TooltipContent>
                    <Styled.TooltipFooter>
                      <TextButton
                        className="button"
                        testId="data-overview-dataset-learn-more-button"
                        onClick={() => window.open(item.url, '_blank')}>
                        Learn more
                      </TextButton>
                    </Styled.TooltipFooter>
                  </>
                }
                key={item.id}>
                {item.title || 'Untitled'}
              </Chip>
            ))}
          </Styled.TableData>
        </Styled.TableRow>
      </Styled.Table>
      <TextButton
        className="info-button"
        startIcon={<InfoIcon />}
        testId="source-info-button"
        onClick={handleOpenInfo}>
        Find out more about our data
      </TextButton>
    </Styled.Wrapper>
  );
}

export default SourceOverview;
